import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Phone } from "lucide-react";
import Footer from "@/components/Footer";
import { LOGO_URL, PHONE, PHONE_TEL } from "@/lib/siteData";
import products from "@/lib/comparators.json";

const BASE = "https://mdfinancesconseil.oggo-data.net";
export const LAST_KEY = "md_oggo_last_product";

function OggoWidget({ kind, code, height }) {
  const ref = useRef(null);
  const [h, setH] = useState(null);
  const { search } = useLocation();
  const src = useMemo(() => {
    if (kind === "iform") return `${BASE}/iform/${code}`;
    const key = `md_oggo_uuid_${code}`;
    const uuid = new URLSearchParams(search).get("uuid") || localStorage.getItem(key) || "";
    if (uuid) localStorage.setItem(key, uuid);
    return `${BASE}/icomparator/${code}?uuid=${encodeURIComponent(uuid)}`;
  }, [kind, code, search]);
  useEffect(() => {
    setH(null);
    const onMsg = (e) => {
      if (e.source !== ref.current?.contentWindow || e.data?.type !== "oggodata-iframe-height") return;
      const v = parseInt(e.data.height, 10);
      if (v >= 120 && v <= 60000) setH(Math.max(v + 20, height));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [kind, height, src]);
  const fallback = kind === "iform" ? `${height}px` : "max(640px, calc(100vh - 120px))";
  return (
    <iframe ref={ref} key={src} src={src} title={`Comparateur ${code}`} data-testid={`oggo-${kind}-${code}`}
      className="w-full block border-0" style={{ height: h ? `${h}px` : fallback }} />
  );
}

function useMeta(title, description, noindex) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", `https://mdcourtage.fr${window.location.pathname}`);
    const robots = document.querySelector('meta[name="robots"]');
    const prevRobots = robots?.getAttribute("content");
    if (robots && noindex) robots.setAttribute("content", "noindex, follow");
    window.scrollTo(0, 0);
    return () => { document.title = prev; if (robots && prevRobots) robots.setAttribute("content", prevRobots); };
  }, [title, description, noindex]);
}

export function ComparatorShell({ testId, children }) {
  return (
    <div className="bg-md-bg min-h-screen text-md-text" data-testid={testId}>
      <header className="bg-white/90 backdrop-blur-md border-b border-md-border sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/"><img src={LOGO_URL} alt="MD Courtage" className="h-12 object-contain" /></Link>
          <div className="flex items-center gap-4">
            <a href={`tel:${PHONE_TEL}`} className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-md-primary"><Phone className="w-4 h-4" /> {PHONE}</a>
            <a href="/comparateur" data-testid="comparator-hub-link" className="text-sm text-md-muted hover:text-md-primary">Tous les comparateurs</a>
          </div>
        </div>
      </header>
      {children}
      <Footer />
    </div>
  );
}

export default function ComparatorPage({ results = false }) {
  const { product } = useParams();
  const p = products.find((x) => x.slug === product);
  useMeta(
    p ? `${results ? "Vos offres" : "Comparateur"} ${p.name} — devis en ligne | MD Courtage` : "MD Courtage",
    p ? `${p.desc} Comparez gratuitement les offres et faites-vous accompagner par votre courtier MD Courtage.` : "",
    results,
  );
  useEffect(() => { if (p && !results) localStorage.setItem(LAST_KEY, p.slug); }, [p, results]);
  if (!p) return <Navigate to="/comparateur" replace />;

  return (
    <ComparatorShell testId={`comparator-${results ? "results" : "form"}-${p.slug}`}>
      <main className="max-w-6xl mx-auto px-6 py-12 md:py-16">
        <a href={results ? `/comparateur/${p.slug}` : "/comparateur"} className="inline-flex items-center gap-1.5 text-sm text-md-muted hover:text-md-primary mb-6"><ArrowLeft className="w-4 h-4" /> {results ? "Modifier ma demande" : "Tous les comparateurs"}</a>
        <div className="text-xs uppercase tracking-[0.25em] text-md-accent font-bold mb-3">{p.group}</div>
        <h1 data-testid="comparator-h1" className="font-serif text-4xl sm:text-5xl text-md-primary font-medium leading-tight mb-4">
          {results ? `Vos offres ${p.name.toLowerCase()}` : `Comparateur ${p.name.toLowerCase()}`}
        </h1>
        <p className="text-md-muted max-w-3xl mb-8">{results ? "Voici les offres correspondant à votre profil. Besoin d'un avis avant de choisir ? Je vous rappelle gratuitement pour vous aider à comparer les garanties." : p.desc}</p>
        <div className="bg-white border border-md-border rounded-3xl p-4 md:p-8">
          <OggoWidget kind={results ? "icomparator" : "iform"} code={p.code} height={results ? 640 : p.formHeight} />
        </div>
        <p className="flex items-start gap-2 text-xs text-md-muted mt-6 max-w-3xl">
          <ShieldCheck className="w-4 h-4 text-md-accent flex-shrink-0" />
          <span>Comparateur fourni par Oggo Data pour MD Courtage, courtier en assurance (Orias n° 26006910). Les informations saisies sont
          transmises à Oggo Data et aux assureurs pour établir les tarifs. Les offres présentées ne couvrent pas l'ensemble du marché.
          Voir la <a href="/confidentialite" className="underline">politique de confidentialité</a>.</span>
        </p>
      </main>
    </ComparatorShell>
  );
}
